import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useCallback, useState} from 'react';
import areEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {ProcessFlowchartEdgeConfigSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {createLegacyGraphbuilderNodeFromFlowchartNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {findAllDownstreamMappableNodeIds} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  ProcessGraphBuilderNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderTaskNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {TransitionSource} from 'processes/modals/field-mapping/transition-modal-with-flowchart-editor/transitionModalWithFlowchartEditorConstants';
import {useRouterConfigForm, RouterConfigForm} from 'processes/modals/router-config/RouterConfigModal';
import {
  getDefaultTransitionSource,
  getTransitionSourceByNodeIdPairKey,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import usePrevious from 'ui-library/hooks/usePrevious';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  RouterNode,
  TaskSchemaNode,
  isInteractivePlaceholderNode,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange} from '../processFlowchartConfigEditorUtils';

function RouterNodeDetailsSidepanelWithValidRouter(props: {
  selectedNode: RouterNode;
  inputNodes: Array<ProcessGraphBuilderNode>;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  transitionSource: TransitionSource;
}): JSX.Element {
  const {flowchartTaskSchema, allSchemasById, onChangeFlowchart, interactiveFlowchart} =
    NodeDetailsSidepanelContext.useContext();

  const {functionSpecs, defaultFunctionSpec, name, submitDisabledMessage, ...routerConfigFormState} =
    useRouterConfigForm({
      inputNodes: props.inputNodes,
      routerConfig: props.selectedNode.data.routerConfig,
      analyticsMetadata: {},
      flowchartSchema: flowchartTaskSchema,
      routerNodeClientId: props.selectedNode.id,
    });

  const [routerConfig, setRouterConfig] = useState({
    clientId: props.selectedNode.data.routerConfig.clientId,
    name,
    defaultFunctionSpec,
    functionSpecs,
  });
  const prevRouterConfig = usePrevious(routerConfig);

  useEffect(() => {
    setRouterConfig({
      clientId: props.selectedNode.data.routerConfig.clientId,
      name,
      defaultFunctionSpec,
      functionSpecs,
    });
  }, [functionSpecs, defaultFunctionSpec, name, props.selectedNode.data.routerConfig.clientId]);

  const getMatchingEdgeId = useCallback(
    (edgeConfigReference: ProcessFlowchartEdgeConfigSpec): string => {
      // The router config modal uses the Graphbuilder edge client ID convention so edgeConfigReference.clientId
      // does not actually match an edge in the interactiveFlowchart. So instead we find the edge which matches
      // the from/to node client IDs.
      const matchingEdge = interactiveFlowchart.edges.find(
        (edge) =>
          edge.source === edgeConfigReference.fromNodeClientId &&
          edge.target === edgeConfigReference.toNodeClientId
      );
      assert(matchingEdge != null, 'Matching edge not found');
      return matchingEdge.id;
    },
    [interactiveFlowchart.edges]
  );

  useEffect(() => {
    // When the router config has changed and is valid we apply the changes to the flowchart.
    if (submitDisabledMessage != null) {
      return;
    }

    if (areEqual(prevRouterConfig, routerConfig)) {
      return;
    }

    // Changes to the router config may change the labels on the edges.
    const edgeLabelChanges: Array<FlowchartEdgeChange> = compact([
      ...routerConfig.functionSpecs.map((functionSpec) =>
        functionSpec.edgeConfigReference != null
          ? {
              type: 'label' as const,
              id: getMatchingEdgeId(functionSpec.edgeConfigReference),
              label: functionSpec.name,
            }
          : null
      ),
      routerConfig?.defaultFunctionSpec?.edgeConfigReference != null
        ? {
            type: 'label' as const,
            id: getMatchingEdgeId(routerConfig.defaultFunctionSpec.edgeConfigReference),
            label: routerConfig.defaultFunctionSpec.name,
          }
        : null,
    ]);
    onChangeFlowchart({
      nodeChanges: [
        {
          type: 'replace',
          id: props.selectedNode.id,
          data: {
            ...props.selectedNode.data,
            nodeName: routerConfig.name,
            routerConfig,
          },
        },
      ],
      edgeChanges: edgeLabelChanges,
    });
  }, [
    prevRouterConfig,
    onChangeFlowchart,
    routerConfig,
    props.selectedNode.data,
    props.selectedNode.id,
    submitDisabledMessage,
    getMatchingEdgeId,
  ]);

  const {inputNode} = routerConfigFormState;

  const inputTaskSchema = allSchemasById[inputNode.taskSchemaId] as ProcessTaskSchemaGraphQL;
  assert(inputTaskSchema != null, 'Input task schema not found');
  const inputSchema =
    props.transitionSource === TransitionSource.INPUTS ? inputTaskSchema : inputTaskSchema.outputSchema;
  assert(inputSchema != null, 'Input schema not found');

  return (
    <>
      <RouterConfigForm
        {...{...routerConfigFormState, allSchemasById}}
        functionSpecs={functionSpecs}
        defaultFunctionSpec={defaultFunctionSpec}
        name={name}
        submitDisabledMessage={submitDisabledMessage}
        routerNodeClientId={props.selectedNode.id}
        flowchartClientId=""
        analyticsMetadata={{}}
        immediateChildNodes={props.immediateChildNodes}
        didInitialFlowchartHaveChainedRouters={false}
        doesCurrentFlowchartHaveChainedRouters={false}
        flowchartTaskSchema={flowchartTaskSchema}
        inputSchema={inputSchema}
      />
      {/*
       * This is a temporary solution for displaying errors in the router configuration
       * that prevent saving the current changes into the flowchart state.
       * This banner may not be in view of the user since it's below the form.
       */}
      {submitDisabledMessage && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="The router configuration has not been saved"
          supportingInfo={submitDisabledMessage}
        />
      )}
    </>
  );
}

export default function RouterNodeDetailsSidepanelContent({
  selectedNode,
}: {
  selectedNode: RouterNode;
}): JSX.Element {
  const {interactiveFlowchart, transitionSourceByNodeIdPair} = NodeDetailsSidepanelContext.useContext();

  const inputFlowchartNodes = interactiveFlowchart.edges
    .filter((edge) => edge.target === selectedNode.id)
    .map((edge) => {
      const inputNode = interactiveFlowchart.nodes.find((node) => node.id === edge.source);
      assert(inputNode != null, 'Input node not found');
      return inputNode;
    });

  const immediateChildFlowchartNodes = interactiveFlowchart.edges
    .filter((edge) => edge.source === selectedNode.id)
    .map((edge) => {
      const childNode = interactiveFlowchart.nodes.find((node) => node.id === edge.target);
      assert(childNode != null, 'Child node not found');
      return childNode;
    });

  const getErrorMessage = (): string | null => {
    if (inputFlowchartNodes.length === 0) {
      return 'Cannot edit the router configuration because there are no inputs to the router.';
    } else if (inputFlowchartNodes.length > 1) {
      return 'Cannot edit the router configuration because there are multiple inputs to the router.';
    } else if (immediateChildFlowchartNodes.length < 2) {
      return 'Cannot edit the router configuration because there are fewer than 2 outputs of the router.';
    } else if (inputFlowchartNodes.some((node) => isInteractivePlaceholderNode(node))) {
      return 'Cannot edit the router configuration because the input has not been fully configured.';
    } else if (immediateChildFlowchartNodes.some((node) => isInteractivePlaceholderNode(node))) {
      return 'Cannot edit the router configuration because one or more of the downstream nodes have not been fully configured.';
    }
    return null;
  };

  const errorMessage = getErrorMessage();
  if (errorMessage != null) {
    return <div className={css(styles.errorMessage)}>{errorMessage}</div>;
  }

  const inputNodes = inputFlowchartNodes.map(
    (inputNode: TaskSchemaNode) =>
      createLegacyGraphbuilderNodeFromFlowchartNode(inputNode) as
        | ProcessGraphBuilderTaskNode
        | ProcessGraphBuilderRootNode
  );

  const immediateChildNodes = immediateChildFlowchartNodes.map((childNode) =>
    createLegacyGraphbuilderNodeFromFlowchartNode(childNode)
  );

  const inputFlowchartNode = inputFlowchartNodes[0];
  assert(inputFlowchartNode != null, 'Input node not found');
  const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
  const transitionSources = new Set<TransitionSource>();
  for (const nodeId of downstreamMappableNodeIds) {
    const transitionSource =
      transitionSourceByNodeIdPair[getTransitionSourceByNodeIdPairKey(inputFlowchartNode.id, nodeId)] ??
      getDefaultTransitionSource(
        inputFlowchartNode.id,
        interactiveFlowchart.nodes.map(({data}) => data)
      );
    transitionSources.add(transitionSource);
  }
  if (transitionSources.size > 1) {
    const downstreamNodeNames = downstreamMappableNodeIds
      .map((nodeId) => {
        const node = interactiveFlowchart.nodes.find((node) => node.id === nodeId);
        assert(node != null, 'Node not found');
        return node.data.nodeName;
      })
      .sort((leftName, rightName) => leftName.localeCompare(rightName))
      .join(', ');
    return (
      <div className={css(styles.errorMessage)}>
        Cannot edit the router configuration because the the downstream nodes {downstreamNodeNames} map from
        different sources in {inputFlowchartNode.data.nodeName}. All nodes must map from the same data source
        to configure a router.
      </div>
    );
  }

  const transitionSource = [...transitionSources][0];

  return (
    <RouterNodeDetailsSidepanelWithValidRouter
      selectedNode={selectedNode}
      inputNodes={inputNodes}
      immediateChildNodes={immediateChildNodes}
      transitionSource={transitionSource}
    />
  );
}

const styles = StyleSheet.create({
  errorMessage: {
    padding: 16,
  },
});
